import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { GridContext } from '..';
import { KakarikoDates } from '../../Inputs/Dates';
import { KakarikoEditingCell } from '../../Inputs/EditingCell';
import { KakarikoSelect } from '../../Inputs/Select';
import { KakarikoText } from '../../Inputs/Text';
import {
  deepClone,
  flatStateReducer,
  retrieveNestedValue,
  setNestedValue,
} from '../../utils';
import { KakarikoGridContextType } from '../types';
import { StyledKakarikoGridRow } from './styles';
import { IKakarikoGridRowProps } from './types';

export const KakarikoGridRow: React.FC<IKakarikoGridRowProps> = (
  props: IKakarikoGridRowProps,
) => {
  const {
    children,
    data,
    handleDataUpdate = () => {},
    handleEditingState: propsHandleEditingState,
    isEditing: propsIsEditing,
    updateDataRow,
    $textAlignment,
  } = props;

  const { variant } = React.useContext<KakarikoGridContextType>(GridContext);

  const [localData, setLocalData] = useState(deepClone(data));
  const [tempData, setTempData] = useState(deepClone(data));
  const [internalErrors, setInternalErrors] = useReducer<
    React.Reducer<booleanGenericObject, booleanGenericObject>
  >(flatStateReducer, {});

  const [isChanged, setChangedState] = useState(false);
  const [isEditing, setEditingState] = useState(propsIsEditing ?? false);
  const [isSaving, setSavingState] = useState(false);

  const validations = useRef({});

  const editableTypes = [
    KakarikoDates.name,
    KakarikoEditingCell.name,
    KakarikoText.name,
    KakarikoSelect.name,
  ];
  const updatableTypes = [KakarikoEditingCell.name];

  useEffect(() => {
    // If propsIsEditing is not nullish.
    if (propsIsEditing != null) {
      setEditingState(propsIsEditing);
      setTempData(deepClone(localData));
    }
  }, [propsIsEditing, localData]);

  const handleChange = useCallback(
    (field: string | string[], value: any) => {
      setTempData({ ...setNestedValue(field, tempData, value) });
      setChangedState(true);
    },
    [tempData, setChangedState, setTempData],
  );

  const retrieveValue = useCallback(
    (name: string) => {
      return retrieveNestedValue(name, tempData);
    },
    [tempData],
  );

  // Run validations on each field whenever the data changes.
  useEffect(() => {
    Object.entries(validations.current).forEach(([field, fns]) => {
      if (!Array.isArray(fns)) return;
      fns.forEach((fn) => {
        const valid = fn(retrieveValue(field));
        setInternalErrors({ [field]: !valid });
      });
    });
  }, [tempData, retrieveValue]);

  const isEditable = useCallback((element: React.ReactElement) => {
    if (typeof element.type === 'string' || !element.type?.name) return false;
    return editableTypes.includes(element.type.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEditAction = useCallback((element: React.ReactElement) => {
    if (typeof element.type === 'string' || !element.type?.name) return false;
    return updatableTypes.includes(element.type.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditingState = useCallback(() => {
    if (propsHandleEditingState) {
      propsHandleEditingState(!isEditing);
    } else {
      setEditingState(!isEditing);
      setTempData(deepClone(localData));
    }
  }, [
    isEditing,
    localData,
    propsHandleEditingState,
    setEditingState,
    setTempData,
  ]);

  const handleSaveState = useCallback(async () => {
    setSavingState(true);
    const newData = {
      ...deepClone(localData),
      ...deepClone(tempData),
    };
    const successful = await handleDataUpdate(newData);
    if (successful) {
      setLocalData(deepClone(newData));
      if (updateDataRow) updateDataRow(newData);
    } else {
      setTempData(deepClone(localData));
    }
    if (propsHandleEditingState) propsHandleEditingState(!successful);
    setEditingState(false);
    setSavingState(false);
  }, [
    localData,
    tempData,
    handleDataUpdate,
    propsHandleEditingState,
    setLocalData,
    setEditingState,
    setSavingState,
    updateDataRow,
  ]);

  const clone = useCallback(
    (
      element: React.ReactElement,
      key: number | string,
      props?: { [key: string]: any },
    ) => {
      const child = element.props?.children;

      const shouldPassProps = (child: any) =>
        !!(child && (isEditable(child) || isEditAction(child)));
      const gridElement = child || element;
      const name = gridElement.props?.name;
      if (name) {
        validations.current = {
          ...validations.current,
          [name]: gridElement.props.validation,
        };
      }

      const names = gridElement.props?.names;
      if (names) {
        names.forEach((name: string, index: number) => {
          validations.current = {
            ...validations.current,
            [name]: Array.isArray(gridElement.props.validation[index])
              ? gridElement.props.validation[index]
              : null,
          };
        });
      }

      const hasError =
        gridElement.props?.validation &&
        (gridElement.props?.hasError ||
          internalErrors[name] ||
          names
            ?.map((name: string) => ({ [name]: internalErrors?.[name] }))
            .reduce((acc: any, curr: any) => ({ ...acc, ...curr })));

      const passedProps = {
        key,
        className: 'cell',
        'data-testid': 'grid-cell',
        ...(!isEditing && { tabIndex: 0 }),
        ...props,
        ...(isEditable(gridElement) && {
          hasError,
          isDisabled:
            !isChanged || Object.values(internalErrors).some((e) => e === true),
          isEditing: isEditing,
          isSaving: isSaving,
          handleChange:
            gridElement.props?.handleChange ??
            ((e: string) => handleChange(name ?? names, e)),
          ...((name || names) && {
            value:
              gridElement.props?.value ??
              retrieveNestedValue(name ?? names, tempData),
          }),
          ...(variant && { variant: variant }),
        }),
        ...(isEditAction(gridElement) && {
          hasError,
          isUpdated: isSaving,
          handleCancel: handleEditingState,
          handleEditingState,
          handleSaveState,
        }),
      };

      return React.cloneElement(element, {
        key,
        className: 'cell',
        ...(!isEditing && { tabIndex: 0 }),
        ...(shouldPassProps(child)
          ? { passedProps: passedProps }
          : { ...passedProps }),
        ...props,
      });
    },
    [
      isChanged,
      internalErrors,
      variant,
      isEditing,
      isSaving,
      tempData,
      handleChange,
      handleEditingState,
      handleSaveState,
      isEditable,
      isEditAction,
    ],
  );

  return (
    <StyledKakarikoGridRow
      data-testid="grid-row"
      className={clsx({ editing: isEditing })}
      $textAlignment={$textAlignment}
    >
      {children
        .filter((child: React.ReactElement | boolean) => !!child)
        .map((child: React.ReactElement, index: number) =>
          clone(child, `${data?.id}-${index}`),
        )}
    </StyledKakarikoGridRow>
  );
};
